import { requestWorkspaceExit } from '../../core/workspace/useWorkspaceDraftGuard';
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api";
import { productOrigin } from "../../utils/productOrigins";
import { accessUserId } from "../../utils/auth";
import "./GlobalAccountMenu.css";

// Authentication is owned by the Marketplace account authority, not by the
// product build's REACT_APP_SITE_URL. Gateway/Space deployments legitimately
// have their own site URL, so deriving auth from SITE_URL creates a redirect
// loop back to /?target=... on those hosts.
const ACCOUNT_ORIGIN = productOrigin(process.env.REACT_APP_ACCOUNT_URL, 3002, "https://www.thedoar.com");

function centralLogoutAction() {
  try {
    const base = new URL(String(api.defaults.baseURL || productOrigin(process.env.REACT_APP_ACCOUNT_API_URL, 5000, "https://api.thedoar.com")), window.location.origin);
    return `${productOrigin(base.origin, 5000, "https://api.thedoar.com")}/api/auth/handoff/logout`;
  } catch {
    return `${productOrigin(process.env.REACT_APP_ACCOUNT_API_URL, 5000, "https://api.thedoar.com")}/api/auth/handoff/logout`;
  }
}

function submitCentralLogout() {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = centralLogoutAction();
  form.style.display = "none";
  const target = document.createElement("input");
  target.type = "hidden";
  target.name = "targetOrigin";
  target.value = window.location.origin;
  form.appendChild(target);
  document.body.appendChild(form);
  form.submit();
}

function planLabel(value) {
  const raw = String(value || "free_doars").toLowerCase();
  if (raw === "thesuperdoars") return "Super DoAR";
  if (raw === "thedoars") return "DoAR";
  if (raw === "doar_lite") return "DoAR Lite";
  if (raw === "free_doars") return "Free";
  return raw.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function safeAvatarUrl(user) {
  const value = user?.avatarUrl || user?.picture || user?.photoURL || user?.profileImage || user?.profileImageUrl || null;
  if (!value) return null;
  try {
    const url = new URL(String(value), window.location.origin);
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

function Avatar({ url, initial, large = false }) {
  return <span className={`global-account-avatar-media ${large ? "large" : ""}`} aria-hidden="true">
    {url ? <img src={url} alt="" referrerPolicy="no-referrer" /> : <b>{initial}</b>}
  </span>;
}

function hideAccountChrome(pathname) {
  const host = String(window.location.hostname || "").toLowerCase();
  const surface = String(process.env.REACT_APP_PUBLIC_SURFACE || "").toLowerCase();
  const theDoarSurface = host === "thedoar.com"
    || host === "www.thedoar.com"
    || host === "localhost-marketplace"
    || surface === "marketplace";
  const theDoarPublicPath = pathname === "/"
    || pathname === "/marketplace"
    || /^\/(?:r|m)\//.test(pathname);
  const productOwnedChrome = pathname === "/do"
    || pathname === "/ask"
    || /^\/c\//.test(pathname)
    || pathname === "/space"
    || /^\/(?:space|d)\//.test(pathname);

  return /^\/embed\//.test(pathname)
    || productOwnedChrome
    || (theDoarSurface && theDoarPublicPath)
    || pathname === "/login"
    || pathname === "/register"
    || pathname === "/reset-password"
    || pathname === "/verify-email"
    || pathname === "/auth/handoff"
    || pathname === "/auth/complete";
}

export default function GlobalAccountMenu() {
  const { token } = useContext(AuthContext);
  // Profile, usage and edit drafts belong to one account, not to the browser
  // tab. A token refresh keeps the same key; an account switch resets them.
  return <AccountMenu key={accessUserId(token) || "anonymous"} />;
}

function AccountMenu() {
  const { token, userInfo, loading, logout, fetchMe } = useContext(AuthContext);
  const location = useLocation();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState("");
  const [usernameDraft, setUsernameDraft] = useState("");
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState("");
  const hidden = hideAccountChrome(location.pathname);
  const onBillingPage = location.pathname === "/billing";

  useEffect(() => {
    const root = document.documentElement;
    if (hidden) {
      delete root.dataset.doarAccount;
      return undefined;
    }
    root.dataset.doarAccount = loading ? "loading" : token ? "signed-in" : "guest";
    return () => { delete root.dataset.doarAccount; };
  }, [hidden, loading, token]);

  useEffect(() => {
    if (!token) { setProfile(null); return; }
    let live = true;
    api.get("/auth/handoff/profile")
      .then(({ data }) => {
        if (!live) return;
        const user = data?.user;
        setProfile(user && String(user.id || user._id || "").toLowerCase() === accessUserId(token) ? user : null);
      })
      .catch(() => { if (live) setProfile(null); });
    return () => { live = false; };
  }, [token]);

  useEffect(() => {
    if (!token || !open) return;
    let live = true;
    api.get("/model/usage")
      .then(({ data }) => { if (live) setUsage(data || null); })
      .catch(() => { if (live) setUsage(null); });
    return () => { live = false; };
  }, [token, open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.key === "Escape") { setOpen(false); setEditing(false); }
      if (event.type === "pointerdown" && !rootRef.current?.contains(event.target)) { setOpen(false); setEditing(false); }
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", close, true);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", close, true);
    };
  }, [open]);

  if (hidden) return null;

  const beginSignIn = () => {
    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    try { sessionStorage.setItem("doar_return_path", returnPath); } catch {}
    const currentHost = window.location.hostname.toLowerCase();
    if (currentHost === "thedoar.com" || currentHost === "www.thedoar.com" || window.location.origin === ACCOUNT_ORIGIN) {
      const url = new URL("/login", ACCOUNT_ORIGIN);
      if (returnPath && returnPath !== "/") url.searchParams.set("next", returnPath);
      window.location.assign(url.toString());
      return;
    }
    window.location.assign(`${ACCOUNT_ORIGIN}/auth/handoff?target=${encodeURIComponent(window.location.origin)}`);
  };

  if (loading) {
    return <div className="global-account global-account-loading" aria-hidden="true"><span /></div>;
  }

  if (!token) {
    return <div className="global-account"><button type="button" className="global-account-signin" onClick={beginSignIn}>Sign in</button></div>;
  }

  const account = { ...(userInfo || {}), ...(profile || {}) };
  const displayName = account.displayName || account.username || account.name || account.email || "Account";
  const email = account.email || "";
  const plan = planLabel(usage?.plan || account.plan);
  const remaining = usage?.creationCreditsRemaining ?? usage?.aiCreditsRemaining;
  const allowed = usage?.creationCreditsAllowed ?? usage?.aiCreditsAllowed;
  const pct = allowed > 0 && remaining != null ? Math.max(0, Math.min(100, (remaining / allowed) * 100)) : 0;
  const storageUsed = usage?.storageUsedBytes ?? null;
  const storageAllowed = usage?.storageAllowedBytes ?? null;
  const storagePct = storageAllowed > 0 && storageUsed != null ? Math.max(0, Math.min(100, (storageUsed / storageAllowed) * 100)) : 0;
  const formatGb = bytes => bytes == null ? "—" : `${(Number(bytes) / 1024 ** 3).toFixed(Number(bytes) >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
  const initial = String(displayName).trim().slice(0, 1).toUpperCase() || "D";
  const avatarUrl = safeAvatarUrl(account);

  const beginEdit = () => {
    setDisplayNameDraft(account.displayName || account.username || "");
    setUsernameDraft(account.username || "");
    setProfileError("");
    setEditing(true);
  };
  const saveProfile = async (event) => {
    event.preventDefault();
    if (profileBusy) return;
    setProfileBusy(true);
    setProfileError("");
    try {
      const { data } = await api.patch("/auth/handoff/profile", {
        displayName: displayNameDraft,
        username: usernameDraft,
      });
      setProfile(data?.user || null);
      await fetchMe?.().catch?.(() => {});
      setEditing(false);
    } catch (error) {
      setProfileError(error?.response?.data?.message || "Profile could not be saved.");
    } finally {
      setProfileBusy(false);
    }
  };
  const goAccount = async (path) => {
    if (!await requestWorkspaceExit()) return;
    setOpen(false);
    window.location.assign(`${ACCOUNT_ORIGIN}${path}`);
  };
  const signOut = async () => {
    setOpen(false);
    if (await logout({ remote: false }) === false) return;
    submitCentralLogout();
  };

  return (
    <div className="global-account" ref={rootRef}>
      <button
        type="button"
        className="global-account-avatar"
        aria-label="Open account menu"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => { setOpen(v => !v); setEditing(false); }}
        title={`${displayName} · ${plan}`}
      >
        <Avatar url={avatarUrl} initial={initial} />
        <span className="global-account-status" aria-hidden="true" />
      </button>

      {open ? (
        <div className="global-account-popover" role="dialog" aria-label="DoAR account">
          <div className="global-account-identity">
            <Avatar url={avatarUrl} initial={initial} large />
            <div><strong>{displayName}</strong><span>{email}</span><small>{account.emailVerified ? "Verified account" : "Email not verified"}</small></div>
          </div>

          {editing ? (
            <form className="global-account-profile-form" onSubmit={saveProfile}>
              <label><span>Display name</span><input value={displayNameDraft} maxLength={80} onChange={event => setDisplayNameDraft(event.target.value)} autoComplete="name" /></label>
              <label><span>Username</span><input value={usernameDraft} maxLength={30} onChange={event => setUsernameDraft(event.target.value)} autoComplete="username" /></label>
              {profileError ? <p role="alert">{profileError}</p> : null}
              <div><button type="button" onClick={() => setEditing(false)} disabled={profileBusy}>Cancel</button><button type="submit" className="primary" disabled={profileBusy || !usernameDraft.trim()}>{profileBusy ? "Saving…" : "Save"}</button></div>
            </form>
          ) : null}

          {!onBillingPage ? <div className="global-account-plan">
            <div className="global-account-plan-row"><span>{plan} plan</span><button type="button" onClick={() => goAccount("/billing")}>Manage</button></div>
            <div className="global-account-credit-row">
              <strong>{remaining == null ? "—" : Number(remaining).toLocaleString()}</strong>
              <span>{allowed == null ? "Creation Credits" : `of ${Number(allowed).toLocaleString()} Creation Credits left`}</span>
            </div>
            {allowed > 0 ? <div className="global-account-credit-bar"><i style={{ width: `${pct}%` }} /></div> : null}
            <div className="global-account-credit-row"><strong>{storageUsed == null || storageAllowed == null ? "—" : formatGb(storageUsed)}</strong><span>{storageAllowed == null ? "Cloud storage" : `of ${formatGb(storageAllowed)} storage used`}</span></div>
            {storageAllowed > 0 ? <div className="global-account-credit-bar"><i style={{ width: `${storagePct}%` }} /></div> : null}
          </div> : null}

          <div className="global-account-actions">
            <button type="button" onClick={beginEdit}><span>Edit profile</span><small>Name and username</small></button>
            {!onBillingPage ? <button type="button" onClick={() => goAccount("/billing")}><span>Plan & billing</span><small>Subscription, Creation Credits and invoices</small></button> : null}
            <button type="button" onClick={() => goAccount("/security")}><span>Security</span><small>Password, sessions and verification</small></button>
            <button type="button" className="global-account-signout" onClick={signOut}><span>Sign out</span><small>End this DoAR browser session</small></button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
